// One line of guidance for an analytics_query failure the API reports only as
// a position in the request: a JSON key the Query message does not have, an
// enum value that does not exist, or a join along an edge in the wrong
// direction. Other failures already say what to do
// (narrow the window, add a LIKE term, lower the limit) and get nothing here.
// The live schema the chat loaded says what is valid: the keys its runnable
// examples use at the same place in a query, and each entity's metrics and
// fields.

import { extractMessage } from "./harnessMemoryCapture.js";

const UNKNOWN_KEY = /unknown field "([^"]+)"/;
const BAD_ENUM = /invalid value for enum field (\w+): "([^"]+)"/;
const UNDECLARED_EDGE = /edge (\w+) from (\w+) to (\w+) is not declared on the graph/;
const MAX_NAMES = 12;

// Stable proto enums the schema payload does not list; the examples only
// show the few values they happen to use.
const OPERATORS = [
    "OPERATOR_EQ", "OPERATOR_NE", "OPERATOR_LT", "OPERATOR_LTE", "OPERATOR_GT", "OPERATOR_GTE",
    "OPERATOR_IN", "OPERATOR_NOT_IN", "OPERATOR_LIKE", "OPERATOR_ILIKE", "OPERATOR_IS_NULL", "OPERATOR_IS_NOT_NULL",
];
const AGGREGATE_OPS = [
    "AGGREGATE_OP_COUNT", "AGGREGATE_OP_COUNT_DISTINCT", "AGGREGATE_OP_SUM", "AGGREGATE_OP_AVG",
    "AGGREGATE_OP_MIN", "AGGREGATE_OP_MAX", "AGGREGATE_OP_P50", "AGGREGATE_OP_P95", "AGGREGATE_OP_P99",
];

// The path of the first object in `value` that has `key` as its own key:
// "" for the top level, "filter" for a key of `filter`, "output.metrics[]"
// for a key of an element of `output.metrics`. null when no object has it.
function pathOf(value, key, path = "") {
    if (Array.isArray(value)) {
        for (const item of value) {
            const found = pathOf(item, key, `${path}[]`);
            if (found !== null) return found;
        }
        return null;
    }
    if (!value || typeof value !== "object") return null;
    if (Object.hasOwn(value, key)) return path;
    for (const [k, child] of Object.entries(value)) {
        const found = pathOf(child, key, path ? `${path}.${k}` : k);
        if (found !== null) return found;
    }
    return null;
}

// The keys of the objects at `path` in `value`, by the same path scheme.
function keysAt(value, path, into = new Set(), at = "") {
    if (Array.isArray(value)) {
        for (const item of value) keysAt(item, path, into, `${at}[]`);
        return into;
    }
    if (!value || typeof value !== "object") return into;
    if (at === path) {
        for (const key of Object.keys(value)) into.add(key);
        return into;
    }
    for (const [k, child] of Object.entries(value)) keysAt(child, path, into, at ? `${at}.${k}` : k);
    return into;
}

// Every value of `key` anywhere in `value`.
function valuesOf(value, key, into = []) {
    if (Array.isArray(value)) {
        for (const item of value) valuesOf(item, key, into);
    } else if (value && typeof value === "object") {
        for (const [k, child] of Object.entries(value)) {
            if (k === key && typeof child === "string") into.push(child);
            else valuesOf(child, key, into);
        }
    }
    return into;
}

function exampleQueries(schema) {
    const examples = Array.isArray(schema?.examples) ? schema.examples : [];
    return examples.map((e) => e?.query || e?.spec).filter((q) => q && typeof q === "object");
}

function distance(a, b) {
    const s = a.toLowerCase();
    const t = b.toLowerCase();
    let prev = Array.from({ length: t.length + 1 }, (_, j) => j);
    for (let i = 1; i <= s.length; i++) {
        const row = [i];
        for (let j = 1; j <= t.length; j++) {
            row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (s[i - 1] === t[j - 1] ? 0 : 1));
        }
        prev = row;
    }
    return prev[t.length];
}

// A name that starts the other (`or` and `orExpr`) is the likeliest meant,
// then the fewest edits.
function nearness(name, candidate) {
    const a = name.toLowerCase();
    const b = candidate.toLowerCase();
    return [a.startsWith(b) || b.startsWith(a) ? 0 : 1, distance(a, b)];
}

function closest(name, candidates) {
    return [...new Set(candidates)]
        .map((c) => [c, nearness(name, c)])
        .sort(([x, nx], [y, ny]) => nx[0] - ny[0] || nx[1] - ny[1] || x.localeCompare(y))
        .slice(0, MAX_NAMES)
        .map(([c]) => c);
}

function isNear(name, candidate) {
    const [prefix, edits] = nearness(name, candidate);
    return prefix === 0 || edits <= 2;
}

// Every path at which an object in `value` has `key` as its own key.
function pathsOf(value, key, path = "", into = new Set()) {
    if (Array.isArray(value)) {
        for (const item of value) pathsOf(item, key, `${path}[]`, into);
    } else if (value && typeof value === "object") {
        if (Object.hasOwn(value, key)) into.add(path);
        for (const [k, child] of Object.entries(value)) pathsOf(child, key, path ? `${path}.${k}` : k, into);
    }
    return into;
}

const quoted = (names) => names.map((n) => `\`${n}\``).join(", ");

function unknownKeyGuidance(key, spec, schema) {
    const examples = exampleQueries(schema);
    const usedAt = new Set();
    for (const query of examples) pathsOf(query, key, "", usedAt);
    // A key can be legal in one place and invalid in another (`stepAlias`
    // belongs in `computed.field`, not `computed`): blame the first place
    // the examples never put it.
    const inSpec = [...pathsOf(spec, key)];
    const at = inSpec.find((p) => !usedAt.has(p)) ?? pathOf(spec, key);
    if (at === null) return null;
    const keys = new Set();
    for (const query of examples) keysAt(query, at, keys);
    keys.delete(key);
    usedAt.delete(at);
    if (!keys.size && !usedAt.size) return null;
    const where = at ? `in \`${at}\`` : "at the top level of the query";
    const parts = [`\`${key}\` is not a field ${where}.`];
    const names = closest(key, keys);
    if (names.length && isNear(key, names[0])) parts.push(`Did you mean \`${names[0]}\`?`);
    if (names.length) parts.push(`The schema's examples use these keys there: ${quoted(names)}.`);
    if (usedAt.size) {
        const places = [...usedAt].slice(0, 2).map((p) => (p ? `\`${p}\`` : "the top level"));
        parts.push(`They use \`${key}\` in ${places.join(" and ")}.`);
    }
    return parts.join(" ");
}

function entityNamesIn(spec) {
    return [...new Set(valuesOf(spec, "entity").filter((v) => v.startsWith("ENTITY_TYPE_")))];
}

function descriptorName(d, key) {
    return typeof d === "string" ? d : d?.[key] || d?.name || null;
}

function descriptorsFor(entities, schema) {
    return (Array.isArray(schema?.entities) ? schema.entities : []).filter((e) => entities.includes(e?.entity));
}

// The entity of the step a metric reference with `value` points at, else
// the query's entities.
function stepEntities(spec, value) {
    const aliases = new Map();
    (function collect(node) {
        if (Array.isArray(node)) return node.forEach(collect);
        if (!node || typeof node !== "object") return;
        if (typeof node.alias === "string" && typeof node.entity === "string") aliases.set(node.alias, node.entity);
        Object.values(node).forEach(collect);
    })(spec);
    let alias = null;
    (function find(node) {
        if (alias !== null) return;
        if (Array.isArray(node)) return node.forEach(find);
        if (!node || typeof node !== "object") return;
        if (node.metric === value && typeof node.stepAlias === "string") { alias = node.stepAlias; return; }
        Object.values(node).forEach(find);
    })(spec);
    if (alias !== null && aliases.has(alias)) return { alias, entities: [aliases.get(alias)] };
    const [first] = aliases.entries();
    return { alias: first?.[0] ?? null, entities: entityNamesIn(spec) };
}

function primaryKeyOf(descriptor) {
    const fields = descriptor?.fields || [];
    const pk = fields.find((f) => f && typeof f === "object" && (f.isPrimaryKey || f.is_primary_key));
    return descriptorName(pk ?? fields[0], "field");
}

// Standard metrics exist for only a few entities; the rest count through a
// computed metric. Pointing at other entities' metrics sends the model the
// wrong way.
function metricGuidance(value, spec, schema) {
    const { alias, entities } = stepEntities(spec, value);
    const descriptors = descriptorsFor(entities, schema);
    const served = entities.length ? entities.map((e) => `\`${e}\``).join(", ") : "the queried entity";
    const names = descriptors.flatMap((e) => (e.metrics || []).map((m) => descriptorName(m, "metric"))).filter((n) => n && n !== value);
    const key = primaryKeyOf(descriptors[0]);
    const computed = key
        ? ` Count rows with a computed metric instead: \`${JSON.stringify({ computed: { field: { stepAlias: alias ?? "<alias>", field: key }, op: "AGGREGATE_OP_COUNT" } })}\` (AGGREGATE_OP_COUNT_DISTINCT for distinct values, SUM/AVG/MIN/MAX on numeric fields).`
        : " Count rows with a computed metric instead: `{\"computed\":{\"field\":{\"stepAlias\":…,\"field\":…},\"op\":\"AGGREGATE_OP_COUNT\"}}`.";
    if (names.length) {
        return `\`${value}\` is not a valid \`metric\`. The metrics ${served} serves, closest first: ${quoted(closest(value, names))}.${computed}`;
    }
    if (!descriptors.length) return null;
    return `\`${value}\` is not a valid \`metric\`. ${served} has no standard metrics.${computed}`;
}

// `op` names two enums: Operator inside a predicate, AggregateOp inside a
// computed metric. Which one the value was meant as follows from where it sits.
function opGuidance(value, spec) {
    let inComputed = false;
    (function find(node, parent) {
        if (Array.isArray(node)) return node.forEach((n) => find(n, parent));
        if (!node || typeof node !== "object") return;
        if (node.op === value && parent === "computed") inComputed = true;
        for (const [k, child] of Object.entries(node)) find(child, k);
    })(spec, "");
    const valid = inComputed ? AGGREGATE_OPS : OPERATORS;
    const parts = [`\`${value}\` is not a valid \`op\`${inComputed ? " for a computed metric" : ""}. Valid values: ${quoted(valid)}.`];
    if (!inComputed && /NOT_I?LIKE$/.test(value)) parts.push("For a negated match wrap the LIKE/ILIKE predicate in `notExpr`.");
    else if (!inComputed && /CONTAINS|STARTS_WITH|ENDS_WITH|MATCH/.test(value)) parts.push("For a substring match use OPERATOR_ILIKE (or OPERATOR_LIKE) with explicit `%` wildcards.");
    return parts.join(" ");
}

function badEnumGuidance(field, value, spec, schema) {
    if (value.startsWith("METRIC_TYPE_")) return metricGuidance(value, spec, schema);
    if (field === "op") return opGuidance(value, spec);
    const entities = entityNamesIn(spec);
    const descriptors = descriptorsFor(entities, schema);
    const served = entities.length ? entities.map((e) => `\`${e}\``).join(", ") : "the queried entity";
    let names = [];
    let lead = "";
    if (value.startsWith("DIMENSION_FIELD_")) {
        names = descriptors.flatMap((e) => (e.fields || []).map((f) => descriptorName(f, "field")));
        lead = `The fields ${served} serves, closest first:`;
    }
    if (!names.filter(Boolean).length) {
        names = exampleQueries(schema).flatMap((query) => valuesOf(query, field));
        lead = `The schema's examples use these values for \`${field}\`:`;
    }
    names = closest(value, names.filter((n) => n && n !== value));
    if (!names.length) return null;
    return `\`${value}\` is not a valid \`${field}\`. ${lead} ${quoted(names)}.`;
}

// The validator names edges and entities in Rust's CamelCase; the schema in
// proto enum names.
const snake = (camel) => camel.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toUpperCase();

function edgeGuidance(edgeName, fromName, toName, schema) {
    const edges = Array.isArray(schema?.edges) ? schema.edges : [];
    const edge = `EDGE_TYPE_${snake(edgeName)}`;
    const from = `ENTITY_TYPE_${snake(fromName)}`;
    const declared = edges.find((e) => e?.edge === edge);
    const parts = [];
    if (declared) {
        const reversed = declared.source === `ENTITY_TYPE_${snake(toName)}` && declared.target === from;
        parts.push(`\`${edge}\` goes from \`${declared.source}\` to \`${declared.target}\`${reversed ? ": the join is reversed, so put that source on the left (root the query there, or join from it)" : ""}.`);
    }
    const outgoing = edges.filter((e) => e?.source === from).map((e) => `\`${e.edge}\` → \`${e.target}\``);
    if (outgoing.length) parts.push(`Edges from \`${from}\`: ${outgoing.slice(0, MAX_NAMES).join(", ")}.`);
    else if (edges.length) parts.push(`\`${from}\` has no outgoing edges.`);
    return parts.length ? parts.join(" ") : null;
}

export function queryErrorGuidance({ errorText, spec, schema }) {
    if (!schema || !spec || typeof spec !== "object") return null;
    const message = extractMessage(errorText).replace(/\\"/g, "\"");
    const badEnum = message.match(BAD_ENUM);
    if (badEnum) return badEnumGuidance(badEnum[1], badEnum[2], spec, schema);
    const unknownKey = message.match(UNKNOWN_KEY);
    if (unknownKey && message.includes("proto:")) return unknownKeyGuidance(unknownKey[1], spec, schema);
    const edge = message.match(UNDECLARED_EDGE);
    if (edge) return edgeGuidance(edge[1], edge[2], edge[3], schema);
    return null;
}

// Wraps analytics_query so such a failure reaches the model with the line
// after the API's own message. Harness memory and graph hints, wrapped
// outside this, follow it.
export function withQueryGuidance(tool, { getSchema }) {
    if (!tool || typeof tool.execute !== "function") return tool;
    return {
        ...tool,
        async execute(toolCallId, args, ...rest) {
            try {
                return await tool.execute(toolCallId, args, ...rest);
            } catch (err) {
                let guidance = null;
                try {
                    const spec = typeof args?.spec === "string" ? JSON.parse(args.spec) : args?.spec;
                    guidance = queryErrorGuidance({ errorText: err?.message ?? String(err), spec, schema: getSchema?.() });
                } catch { guidance = null; }
                if (!guidance) throw err;
                const augmented = err instanceof Error ? err : new Error(String(err));
                try { augmented.message = `${augmented.message}\n\n${guidance}`; } catch { /* frozen message: surface the bare error */ }
                throw augmented;
            }
        },
    };
}
